import { Card } from '@/components/ui/Card';
import { InlineEntityList } from '@/components/roros/InlineEntityList';
import type { InlineFieldDef } from '@/components/roros/InlineEntityList';
import {
  useRorosSwtItems,
  useRorosSwtItemCreate,
  useRorosSwtItemUpdate,
  useRorosSwtItemRemove,
  useRorosSwtItemReorder,
} from '@/hooks/roros/overarching';
import type { RorosSwtItemDTO } from '@/types/roros/overarching';
import { t } from '@/lib/i18n';
import { useOverarchingEditable } from '../OverarchingEditableContext';

// ─── SWT sections (filtered by type) ─────────────────────────────────────────

export function SwtSection({
  title,
  emptyHint,
  swtType,
}: {
  title: string;
  emptyHint?: string;
  swtType: 'strength' | 'weakness' | 'trend';
}) {
  const q = useRorosSwtItems({ type: swtType, pageSize: '100' });
  const createMut = useRorosSwtItemCreate();
  const updateMut = useRorosSwtItemUpdate();
  const removeMut = useRorosSwtItemRemove();
  const reorderMut = useRorosSwtItemReorder();
  const { canEdit } = useOverarchingEditable();

  const items = (((q.data as unknown) ?? []) as RorosSwtItemDTO[]).filter((i) => i.type === swtType);

  const fields: InlineFieldDef<RorosSwtItemDTO>[] = [
    { name: 'title', label: t('Title'), type: 'text', required: true },
    { name: 'description', label: t('Description'), type: 'textarea', rows: 2 },
  ];

  async function handleReorder(index: number, direction: 'up' | 'down') {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    await reorderMut.mutateAsync([
      { id: items[index].id, sortOrder: items[swapIdx].sortOrder },
      { id: items[swapIdx].id, sortOrder: items[index].sortOrder },
    ]);
  }

  return (
    <Card padding="md">
      <InlineEntityList<RorosSwtItemDTO>
        title={title}
        emptyHint={emptyHint}
        items={items}
        fields={fields}
        readOnly={!canEdit}
        onCreate={async (draft) => {
          await createMut.mutateAsync({ ...(draft as Record<string, unknown>), type: swtType });
        }}
        onUpdate={async (id, patch) => { await updateMut.mutateAsync({ id, payload: patch as Record<string, unknown> }); }}
        onDelete={async (id) => { await removeMut.mutateAsync(id); }}
        onReorder={handleReorder}
        renderRow={(item) => (
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{item.title}</p>
            {item.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
            )}
          </div>
        )}
      />
    </Card>
  );
}
